import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';
import { formatPrice } from '../../utils/helpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await axios.delete(`${API}/products/${productId}`);
        fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('Failed to delete product');
      }
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.craft_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-serif text-[#3E2723]">Products</h1>
          <button
            onClick={() => navigate('/admin/products/add')}
            className="flex items-center space-x-2 bg-[#3E2723] text-[#FEFCF9] px-4 sm:px-5 py-2 sm:py-2.5 font-medium hover:bg-[#C4A265] transition-colors text-xs sm:text-sm tracking-wider uppercase"
          >
            <Plus className="w-4 sm:w-5 h-4 sm:h-5" />
            <span>Add Product</span>
          </button>
        </div>

        {/* Search */}
        <div className="bg-[#FEFCF9] border border-[#E8DCC8] p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#C4A265]" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search products by name, category, or craft type..."
              className="w-full pl-12 pr-4 py-3 border border-[#E8DCC8] focus:border-[#C4A265] focus:outline-none"
            />
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-[#FEFCF9]   overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <p className="text-[#C4A265]">Loading products...</p>
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F5EFE6]">
                  <tr>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-[#3E2723]">Image</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-[#3E2723]">Product</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-[#3E2723]">Category</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-[#3E2723]">Price</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-[#3E2723]">Stock</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-[#3E2723]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => (
                    <tr key={product.id} className="border-b border-[#F5F1E8] hover:bg-[#FDFCFA]">
                      <td className="py-4 px-6">
                        <img
                          src={product.images[0] || 'https://via.placeholder.com/80'}
                          alt={product.name}
                          className="w-16 h-16 object-cover "
                        />
                      </td>
                      <td className="py-4 px-6">
                        <p className="font-medium text-[#3E2723]">{product.name}</p>
                        {product.featured && (
                          <span className="text-xs bg-[#C4A265] text-white px-2 py-1">{/* Featured */}Featured</span>
                        )}
                      </td>
                      <td className="py-4 px-6 text-sm text-[#5D4E37]">{product.category}</td>
                      <td className="py-4 px-6 text-sm font-semibold text-[#3E2723]">
                        {formatPrice(product.price)}
                      </td>
                      <td className="py-4 px-6">
                        <span className={`text-sm font-medium ${
                          product.stock > 10 ? 'text-green-600' :
                          product.stock > 0 ? 'text-orange-600' :
                          'text-red-600'
                        }`}>
                          {product.stock}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => navigate(`/admin/products/edit/${product.id}`)}
                            className="p-2 text-[#3E2723] hover:bg-[#3E2723]/10  transition-colors"
                            title="Edit"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(product.id)}
                            className="p-2 text-red-600 hover:bg-red-50  transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <p className="text-[#5D4E37] mb-4">No products found</p>
              <button
                onClick={() => navigate('/admin/products/add')}
                className="text-[#C4A265] font-semibold hover:text-[#3E2723]"
              >
                Add your first product
              </button>
            </div>
          )}
        </div>

        <div className="mt-4 text-sm text-[#5D4E37]">
          Showing {filteredProducts.length} of {products.length} products
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;
